import React from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Label } from '../../../components/Label';
import { useWorkstation } from '@/hooks/useWorkstation';
import { FormItem } from '../../../components/FormItem';
import { SelectWithCreation } from '@/components/ui/Inputs';
import type { InputDefaultProps } from '../types';
import type { ICreateWorkstation } from '@/hooks/useWorkstation/types';

const { useFormInstance, useWatch } = Form;

export function Workstation({
	fieldName,
	isLoading = false,
	data = [],
	isError = false
}: Readonly<InputDefaultProps>): JSX.Element {
	const form = useFormInstance();
	const { useCreateWorkstation } = useWorkstation();

	const organizationId = useWatch([fieldName, 'organization_id'], form);
	const companyId = useWatch([fieldName, 'company_id'], form);
	const lineId = useWatch([fieldName, 'line_id'], form);

	const { mutateAsync: createWorkstation, isLoading: isCreatingWorkstation } = useCreateWorkstation();

	function handleSelectWorkstations(id: string): void {
		form.setFieldValue([fieldName, 'workstation_id'], id);
	}

	function handleClearWorkstations(): void {
		form.setFieldValue([fieldName, 'activity_id'], undefined);
	}

	async function handleCreateWorkstation(name: string): Promise<void> {
		const body: ICreateWorkstation = {
			name,
			line_id: lineId,
			company_id: companyId,
			organization_id: organizationId
		};

		const workstation = await createWorkstation(body);

		if (workstation?.id) {
			handleSelectWorkstations(workstation.id);
		}
	}

	const title = I18n.get('Workstation');

	return (
		<Col xs={24}>
			<Row gutter={[0, 10]}>
				<Col span={24}>
					<Label isRequired>{title}</Label>
				</Col>
				<Col span={24}>
					<FormItem
						name={[fieldName, 'workstation_id']}
						rules={[{ required: true, message: I18n.get('Enter the workstation') }]}
					>
						<SelectWithCreation
							showSearch
							allowClear
							size="large"
							allowCreation
							placeholder={title}
							loading={isLoading}
							selectOptions={data}
							onClear={handleClearWorkstations}
							onChange={handleSelectWorkstations}
							onCreate={handleCreateWorkstation}
							creatingData={isCreatingWorkstation}
							disabled={!lineId || isLoading || isError}
						/>
					</FormItem>
				</Col>
			</Row>
		</Col>
	);
}
